import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { buildDetailedStatRows, buildEfsunRows } from '../../utils/itemStatUtils';
import {
  MAX_WEAPON_EFSUNS,
  MAX_EQUIPMENT_EFSUNS,
  formatEfsunCountLabel,
  getEfsunKindForInventoryType,
  getEfsunKindForSlotType,
  isWeaponEfsunBlocked
} from '../../utils/armorEfsunUtils';
import {
  formatPlatingRemaining,
  getActivePlating
} from '../../utils/ingotPlatingUtils';
import { useCharacter } from '../../context/CharacterContext';
import { farmFoodConfigFromResponse } from '../../utils/farmFoodConfig';
import { readCraftRecipeBook } from '../../utils/craftRecipeBook';
import { tl } from '../../utils/inlineI18n';

const useInventoryItemPopupActions = ({
  username,
  onAfterAction,
  setError,
  setStatus,
  clearError,
  setDepotItemCount,
  refreshCharacter
}) => {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter } = useCharacter();

  const [activeItemPopup, setActiveItemPopup] = useState(null);
  const [sellQuantity, setSellQuantity] = useState(1);
  const [sellLoading, setSellLoading] = useState(false);
  const [fishOpeningConfig, setFishOpeningConfig] = useState(null);
  const [farmFoodConfig, setFarmFoodConfig] = useState(null);
  const [miningConfig, setMiningConfig] = useState(null);
  const [fishOpenLoading, setFishOpenLoading] = useState(false);
  const [fishCookLoading, setFishCookLoading] = useState(false);
  const [fishUseLoading, setFishUseLoading] = useState(false);
  const [cropUseLoading, setCropUseLoading] = useState(false);
  const [lootBoostLoading, setLootBoostLoading] = useState(false);
  const [recipeBookLoading, setRecipeBookLoading] = useState(false);
  const [oreSmeltLoading, setOreSmeltLoading] = useState(false);
  const [bossStomachOpenLoading, setBossStomachOpenLoading] = useState(false);
  const [characterMeta, setCharacterMeta] = useState(null);

  useEffect(() => {
    if (!username) return;
    axios.get('/api/fishing/config')
      .then((res) => {
        if (res.data?.success && res.data?.config?.opening) {
          setFishOpeningConfig(res.data.config.opening);
        }
      })
      .catch(() => {});
    axios.get('/api/farming/config')
      .then((res) => {
        if (res.data?.success) {
          const foodCfg = farmFoodConfigFromResponse(res.data);
          if (foodCfg) setFarmFoodConfig(foodCfg);
        }
      })
      .catch(() => {});
    axios.get('/api/mining/config')
      .then((res) => {
        if (res.data?.success && res.data?.config) {
          setMiningConfig(res.data.config);
        }
      })
      .catch(() => {});
    axios.get(`/api/inventory/bootstrap?username=${encodeURIComponent(username)}&catalog=0`)
      .then((res) => {
        if (res.data?.success) {
          setCharacterMeta({
            characterId: res.data.characterId,
            level: res.data.level
          });
        }
      })
      .catch(() => {});
  }, [username]);

  useEffect(() => {
    if (activeItemPopup?.item) {
      setSellQuantity(1);
    }
  }, [activeItemPopup]);

  const getEffectiveCharacterId = () => userCharacter?.characterId ?? characterMeta?.characterId ?? null;
  const getEffectiveLevel = () => userCharacter?.level ?? characterMeta?.level ?? 1;

  const getCharacterName = (characterId) => {
    if (characterId === 0) return tl('Herkese Uygun', 'All classes');
    if (characterId === 1) return tl('Alp', 'Alp');
    if (characterId === 2) return tl('Kara Alp', 'Dark Alp');
    return tl('Bilinmeyen', 'Unknown');
  };

  const isItemCompatible = (item) => {
    if (!item) return true;
    const characterId = getEffectiveCharacterId();
    if (characterId == null) return true;
    if (item.characterId === 0) return true;
    return item.characterId === characterId;
  };

  const isLevelSufficient = (item) => {
    if (!item) return true;
    return getEffectiveLevel() >= item.level;
  };

  const getEfsunTargetKindForItem = (item) => {
    if (!item || isWeaponEfsunBlocked(item)) return null;
    return getEfsunKindForInventoryType(item.inventoryType)
      || getEfsunKindForSlotType(item.slotType);
  };

  const getEfsunMaxForItem = (item) => {
    const kind = getEfsunTargetKindForItem(item);
    if (!kind) return undefined;
    if (kind === 'weapon') return MAX_WEAPON_EFSUNS;
    return MAX_EQUIPMENT_EFSUNS;
  };

  const renderStatRow = (row, key) => (
    <p key={key}>
      {row.label}: {row.value}
      {row.bonusText ? <span className={row.bonusClass || 'item-plating-stat-bonus'}> {row.bonusText}</span> : null}
    </p>
  );

  const renderItemPlatingBlock = (item) => {
    const plating = getActivePlating(item);
    if (!plating) return null;
    return (
      <p className="inventory-plating-line" style={{ color: plating.color || '#4fc3f7' }}>
        {plating.label || t('platingLabelFallback')}
        {' · '}
        +{plating.bonusPercent}%
        {' · '}
        {formatPlatingRemaining(plating.remainingSec)}
      </p>
    );
  };

  const renderItemNameWithPlating = (name, item, as = 'h4') => {
    const plating = getActivePlating(item);
    const Tag = as;
    if (!plating) return <Tag>{name}</Tag>;
    return (
      <Tag style={{ color: plating.color || undefined }}>
        {name}
        <span className="inventory-plating-name-tag"> {plating.label}</span>
      </Tag>
    );
  };

  const buildItemStatRows = (itemLike) => {
    const plating = getActivePlating(itemLike);
    return buildDetailedStatRows(itemLike, {
      platingBonusPercent: plating?.bonusPercent || 0
    });
  };

  const getFishOpenKind = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return null;
    const id = Number(baseItemId);
    if (id === Number(fishOpeningConfig.caviarOtherItemId)) return 'caviar';
    if (Array.isArray(fishOpeningConfig.openableFishOtherIds) && fishOpeningConfig.openableFishOtherIds.includes(id)) {
      return 'fish';
    }
    return null;
  };

  const canCookFish = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(fishOpeningConfig.cookableFishOtherIds)
      && fishOpeningConfig.cookableFishOtherIds.includes(id);
  };

  const canUseCookedFish = (baseItemId) => {
    if (!fishOpeningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(fishOpeningConfig.cookedFishOtherIds)
      && fishOpeningConfig.cookedFishOtherIds.includes(id);
  };

  const canUseCropFood = (baseItemId) => {
    if (!farmFoodConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(farmFoodConfig.usableCropOtherIds)
      && farmFoodConfig.usableCropOtherIds.includes(id);
  };

  const lootBoostKind = (baseItemId) => {
    const id = Number(baseItemId);
    if (id === 1314) return 'exp';
    if (id === 1315) return 'drop';
    return null;
  };

  const canSmeltOre = (baseItemId) => {
    if (!miningConfig || baseItemId == null) return false;
    const id = Number(baseItemId);
    return Array.isArray(miningConfig.smeltableOreOtherIds)
      && miningConfig.smeltableOreOtherIds.includes(id);
  };

  const smeltInputCount = miningConfig?.smelt?.inputCount ?? miningConfig?.smelt?.orePerAttempt ?? 10;

  const canSmeltOreWithCount = (baseItemId, count) => {
    if (!canSmeltOre(baseItemId)) return false;
    return (Number(count) || 0) >= smeltInputCount;
  };

  const getCookedFishEffectSummary = (baseItemId) => {
    if (!fishOpeningConfig?.cookedFishEffects || baseItemId == null) return null;
    const id = Number(baseItemId);
    const effects = fishOpeningConfig.cookedFishEffects;
    return effects[id] || effects[String(id)] || null;
  };

  const getCropFoodEffectSummary = (baseItemId) => {
    if (!farmFoodConfig?.cropFoodEffects || baseItemId == null) return null;
    const id = Number(baseItemId);
    const effects = farmFoodConfig.cropFoodEffects;
    return effects[id] || effects[String(id)] || null;
  };

  const closeItemPopup = () => setActiveItemPopup(null);

  const handleGridItemClick = (slotItem, index, containerRef) => {
    if (!slotItem || !containerRef?.current) {
      setActiveItemPopup(null);
      return;
    }
    const isSameSlotOpen = activeItemPopup && activeItemPopup.index === index;
    if (isSameSlotOpen) {
      setActiveItemPopup(null);
      return;
    }
    setActiveItemPopup({ index, item: slotItem });
  };

  const handleSellToMarket = async () => {
    if (!activeItemPopup?.item || !username) return;
    const item = activeItemPopup.item;
    const maxCount = item.count || 1;
    const qty = Math.min(Math.max(1, sellQuantity), maxCount);
    setSellLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/inventory/sell', {
        username,
        inventoryType: item.inventoryType,
        userItemId: item.id,
        count: qty
      });
      if (res.data.success) {
        setActiveItemPopup(null);
        setStatus?.(res.data.message || t('statusSellComplete'));
        await onAfterAction?.({ refreshGold: true, goldOverride: res.data.newGold });
      } else {
        setError?.(res.data.message || t('errorSellFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorSellFailed'));
    } finally {
      setSellLoading(false);
    }
  };

  const handleOpenFishItem = async (userItemId) => {
    if (!username || !userItemId || fishOpenLoading || fishCookLoading || fishUseLoading) return;
    setFishOpenLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/fishing/open', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        const reward = res.data.reward;
        const rewardText = reward?.name ? `${reward.name} x${reward.count || 1}` : '';
        setStatus?.(rewardText ? `${res.data.message} (${rewardText})` : (res.data.message || t('statusFishOpened')));
        setActiveItemPopup(null);
        await onAfterAction?.();
      } else {
        setError?.(res.data.message || t('errorFishOpenFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorFishOpenFailed'));
    } finally {
      setFishOpenLoading(false);
    }
  };

  const handleCookFishItem = async (userItemId) => {
    if (!username || !userItemId || fishCookLoading || fishOpenLoading || fishUseLoading) return;
    setFishCookLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/fishing/cook', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        const reward = res.data.reward;
        const rewardText = reward?.name ? `${reward.name} x${reward.count || 1}` : '';
        setStatus?.(rewardText ? `${res.data.message} (${rewardText})` : (res.data.message || t('statusFishCooked')));
        setActiveItemPopup(null);
        await onAfterAction?.();
      } else {
        setError?.(res.data.message || t('errorFishCookFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorFishCookFailed'));
    } finally {
      setFishCookLoading(false);
    }
  };

  const handleUseCookedFishItem = async (userItemId) => {
    if (!username || !userItemId || fishUseLoading || cropUseLoading || lootBoostLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setFishUseLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/fishing/use-cooked', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus?.(res.data.message || t('statusFishUsed'));
        setActiveItemPopup(null);
        await onAfterAction?.();
        await refreshCharacter?.().catch(() => {});
      } else {
        setError?.(res.data.message || t('errorFishUseFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorFishUseFailed'));
    } finally {
      setFishUseLoading(false);
    }
  };

  const handleUseCropFoodItem = async (userItemId) => {
    if (!username || !userItemId || cropUseLoading || lootBoostLoading || fishUseLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setCropUseLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/farming/use-crop', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus?.(res.data.message || t('statusCropUsed'));
        setActiveItemPopup(null);
        await onAfterAction?.();
        await refreshCharacter?.().catch(() => {});
      } else {
        setError?.(res.data.message || t('errorCropUseFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorCropUseFailed'));
    } finally {
      setCropUseLoading(false);
    }
  };

  const handleUseLootBoostItem = async (userItemId) => {
    if (!username || !userItemId || lootBoostLoading || cropUseLoading || fishUseLoading || fishOpenLoading || fishCookLoading || oreSmeltLoading) return;
    setLootBoostLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/inventory/use-loot-boost', {
        username,
        userItemId: Number(userItemId)
      });
      if (res.data.success) {
        setStatus?.(res.data.message || t('statusLootBoostUsed'));
        setActiveItemPopup(null);
        await onAfterAction?.();
        await refreshCharacter?.().catch(() => {});
      } else {
        setError?.(res.data.message || t('errorLootBoostFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorLootBoostFailed'));
    } finally {
      setLootBoostLoading(false);
    }
  };

  const handleSmeltOre = async (baseItemId) => {
    if (!username || !baseItemId || oreSmeltLoading || fishOpenLoading || fishCookLoading || fishUseLoading) return;
    setOreSmeltLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/mining/smelt', {
        username,
        oreOtherItemId: Number(baseItemId)
      });
      if (res.data.success) {
        if (typeof res.data?.depotItemCount === 'number') {
          setDepotItemCount?.(res.data.depotItemCount);
        }
        if (res.data.smelted) {
          setStatus?.(res.data.message || t('statusOreSmelted'));
        } else {
          setError?.(res.data.message || t('statusOreSmeltFailedPartial'));
        }
        setActiveItemPopup(null);
        await onAfterAction?.();
      } else {
        setError?.(res.data.message || t('errorOreSmeltFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorOreSmeltFailed'));
    } finally {
      setOreSmeltLoading(false);
    }
  };

  const handleOpenBossStomach = async (otherId) => {
    if (!username || !otherId || bossStomachOpenLoading) return;
    setBossStomachOpenLoading(true);
    clearError?.();
    try {
      const res = await axios.post('/api/boss-stomach/open', {
        username,
        otherId: Number(otherId)
      });
      if (res.data.success) {
        const loot = Array.isArray(res.data.items) ? res.data.items : [];
        const lootSummary = loot.length > 0
          ? loot.map((item) => `${item.itemName || item.itemType} x${item.count || 1}`).join(', ')
          : '';
        setStatus?.(lootSummary ? `${res.data.message} (${lootSummary})` : (res.data.message || t('statusBossStomachOpened')));
        setActiveItemPopup(null);
        await onAfterAction?.();
      } else {
        setError?.(res.data.message || t('errorBossStomachOpenFailed'));
      }
    } catch (err) {
      setError?.(err.response?.data?.message || err.message || t('errorBossStomachOpenFailed'));
    } finally {
      setBossStomachOpenLoading(false);
    }
  };

  const handleReadRecipeBook = async (otherId) => {
    if (!username || !otherId || recipeBookLoading) return;
    setRecipeBookLoading(true);
    clearError?.();
    try {
      const result = await readCraftRecipeBook(username, otherId);
      if (result.ok) {
        setStatus?.(result.message);
        setActiveItemPopup(null);
        await onAfterAction?.();
      } else {
        setError?.(result.message);
      }
    } finally {
      setRecipeBookLoading(false);
    }
  };

  const buildPopupProps = useCallback(() => {
    const item = activeItemPopup?.item;
    if (!item) return null;
    const baseItemId = item.baseItem?.id;
    const cookedEffect = getCookedFishEffectSummary(baseItemId);
    const cropEffect = getCropFoodEffectSummary(baseItemId);
    return {
      item,
      onClose: closeItemPopup,
      closeLabel: tc('close'),
      renderItemNameWithPlating,
      renderItemPlatingBlock,
      categoryLabel: tc('category'),
      characterName: {
        label: tc('character'),
        value: getCharacterName(item.baseItem.characterId)
      },
      levelLabel: tc('level'),
      enhancementLabel: tc('enhancement'),
      statRows: buildItemStatRows({
        ...item.baseItem,
        categoryLabel: item.categoryLabel,
        count: item.count || 1,
        plating: item.plating
      }),
      renderStatRow,
      efsunCountLabel: getEfsunTargetKindForItem(item)
        && formatEfsunCountLabel(item.efsunlar, getEfsunMaxForItem(item))
        ? formatEfsunCountLabel(item.efsunlar, getEfsunMaxForItem(item))
        : null,
      efsunRows: getEfsunTargetKindForItem(item)
        ? buildEfsunRows(item.efsunlar, getEfsunTargetKindForItem(item) || 'armor')
        : [],
      compatibilityLabel: `${t('popupCompatibility')}: ${isItemCompatible(item.baseItem) && isLevelSufficient(item.baseItem) ? t('selectCompatibilityOk') : t('selectCompatibilityNo')}`,
      fishOpenKind: getFishOpenKind(baseItemId),
      canCookFish: canCookFish(baseItemId),
      canSmeltOre: canSmeltOre(baseItemId),
      canSmeltOreWithCount: canSmeltOreWithCount(baseItemId, item.count),
      smeltInputCount,
      canUseCookedFish: canUseCookedFish(baseItemId),
      cookedFishEffect: cookedEffect ? t('popupCookedFishEffect', { effect: cookedEffect }) : null,
      canUseCropFood: canUseCropFood(baseItemId),
      cropFoodEffect: cropEffect ? t('popupCropFoodEffect', { effect: cropEffect }) : null,
      canUseLootBoost: lootBoostKind(baseItemId) != null,
      lootBoostEffect: lootBoostKind(baseItemId) === 'exp'
        ? t('popupLootBoostEffect', { effect: t('lootBoostExpEffect') })
        : lootBoostKind(baseItemId) === 'drop'
          ? t('popupLootBoostEffect', { effect: t('lootBoostDropEffect') })
          : null,
      isBossStomachOpenable: item.baseItem?.openable && !getFishOpenKind(baseItemId),
      fishOpenLoading,
      fishCookLoading,
      fishUseLoading,
      cropUseLoading,
      lootBoostLoading,
      oreSmeltLoading,
      bossStomachOpenLoading,
      onOpenFish: handleOpenFishItem,
      onCookFish: handleCookFishItem,
      onSmeltOre: handleSmeltOre,
      onUseCookedFish: handleUseCookedFishItem,
      onUseCropFood: handleUseCropFoodItem,
      onUseLootBoost: handleUseLootBoostItem,
      onOpenBossStomach: handleOpenBossStomach,
      isRecipeBook: Boolean(item.baseItem?.recipeBookLevel),
      recipeBookLoading,
      onReadRecipeBook: handleReadRecipeBook,
      readRecipeBookLabel: t('craft:readBook'),
      npcSellLabel: t('popupNpcSell'),
      sellButtonLabel: t('popupSellToMarket'),
      sellQuantity,
      onDecreaseSellQuantity: () => setSellQuantity((q) => Math.max(1, q - 1)),
      onIncreaseSellQuantity: () => setSellQuantity((q) => Math.min(item.count || 1, q + 1)),
      onSellToMarket: handleSellToMarket,
      sellLoading,
      sellTotalLabel: t('popupTotal', {
        amount: (Number(item.baseItem.sellValue ?? item.baseItem.value) || 0) * sellQuantity
      }),
      openFishLabel: t('popupOpenFish'),
      cookFishLabel: t('popupCookFish'),
      smeltOreLabel: t('popupSmeltOre', { count: smeltInputCount }),
      useFishLabel: t('popupUseFish'),
      useCropLabel: t('popupUseCrop'),
      openBossStomachLabel: t('popupOpenBossStomach'),
      decreaseQtyLabel: t('popupDecreaseQty'),
      increaseQtyLabel: t('popupIncreaseQty')
    };
  }, [
    activeItemPopup,
    bossStomachOpenLoading,
    cropUseLoading,
    lootBoostLoading,
    farmFoodConfig,
    fishCookLoading,
    fishOpenLoading,
    fishOpeningConfig,
    fishUseLoading,
    miningConfig,
    oreSmeltLoading,
    recipeBookLoading,
    sellLoading,
    sellQuantity,
    smeltInputCount,
    t,
    tc
  ]);

  return {
    activeItemPopup,
    setActiveItemPopup,
    handleGridItemClick,
    buildPopupProps
  };
};

export default useInventoryItemPopupActions;
